import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Typography } from 'antd';

import { QuestionnaireAnswer } from '@/features/constructor';
import { ProductAnswer } from '@/features/constructorProducts';
import { sortWithNulls } from '@/utils/helpers';
import { useParams } from 'react-router';
import { ConstructorApi as ConstructorQuestionnaireApi } from '@/features/constructor/api';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { SortableItem, SortableList } from '@/components';
import { ConstructorApi as ConstructorProductsApi } from '@/features/constructorProducts/api';
import { StyledPanel } from '@/features/constructorCommon/styled';
import { usePopups } from '@/hooks';

export const AnswerList = ({ list, type, ...props }) => {
  const [answers, setAnswers] = useState([]);
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = usePopups();
  const { t } = useTranslation();

  async function updateAnswerOrder(answerId, order) {
    const initialAnswers = [...answers];

    try {
      if (type === 'questionnaire') {
        await ConstructorQuestionnaireApi.updateAnswerOrder({
          id: answerId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({ questionSectionTypeId: id })
        });
      }
      if (type === 'products') {
        await ConstructorProductsApi.updateAnswerOrder({
          id: answerId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: id })
        });
      }
      showSuccessToast('Answer order is changed');
    } catch (e) {
      setAnswers(initialAnswers);
      showErrorToast(e, true);
    }
  }

  useEffect(() => {
    if (list) {
      setAnswers(
        list?.sort((a, b) => {
          return sortWithNulls(Number(a.sortOrder), Number(b.sortOrder));
        })
      );
    }
  }, [list]);

  return (
    <>
      <Typography.Title level={4}>{t('Answers')}</Typography.Title>

      {answers && type === 'questionnaire' && (
        <div style={{ margin: '16px 0' }}>
          <SortableList list={answers} setList={setAnswers} updateOrder={updateAnswerOrder}>
            {answers.map((answer, index) => (
              <SortableItem
                key={answer.id}
                id={answer.id}
                handle={true}
                value={answer.id}
                isLast={answers.length - 1 === index}
                isFirst={index === 0}
              >
                <Collapse bordered={false}>
                  <Collapse.Panel header={answer.answer} key={answer.id}>
                    <QuestionnaireAnswer answerProp={answer} {...props} />
                  </Collapse.Panel>
                </Collapse>
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}

      {answers && type === 'products' && (
        <div style={{ margin: '16px 0' }}>
          <Collapse accordion>
            {answers.map((answer) => (
              <StyledPanel header={answer.answer} key={answer.id}>
                <ProductAnswer answerProp={answer} />
              </StyledPanel>
            ))}
          </Collapse>
        </div>
      )}
    </>
  );
};
